import { AfterViewInit, Component, Input, NgZone, OnChanges, OnDestroy, SimpleChanges } from '@angular/core';
import Highcharts from 'highcharts';
import { Subject } from 'rxjs';
import { exhaustMap, takeUntil } from 'rxjs/operators';
import {
  CHART_STYLES,
  HighchartsOptions,
} from 'src/app/domain/shared/component/pie-chart-simple/highcharts-common/highcharts-global-const';
import { ChartSyncService } from '../../kpi/components/charts/chart-sync-service.service';

@Component({
  selector: 'app-base-bar-sync-scroll-chart',
  templateUrl: './base-bar-sync-scroll-chart.component.html',
  styleUrls: ['./base-bar-sync-scroll-chart.component.scss'],
})
export class BaseBarSyncScrollChartComponent implements OnChanges, AfterViewInit, OnDestroy {
  @Input() additionalOptions: Partial<HighchartsOptions> = {};
  @Input() chartId: string;

  options: HighchartsOptions;
  chartInstance: Highcharts.Chart;
  showEmptyState = true;
  private destroy$ = new Subject<void>();
  private unsubscribe$ = new Subject<void>();

  constructor(private chartSyncService: ChartSyncService, private zone: NgZone) {}

  ngAfterViewInit(): void {
    this.initializeOptions();
    this.renderChart();
    this.setupSync();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.additionalOptions) {
      this.initializeOptions();
      setTimeout(() => {
        if (document.getElementById(this.chartId)) {
          this.reRenderChart();
        }
      }, 250);
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }

  initializeOptions(): void {
    const defaultOptions: HighchartsOptions = {
      chart: {
        type: 'column',
        events: CHART_STYLES.syncEventForScrolling(this.chartSyncService),
      },
      title: {
        text: undefined,
        style: CHART_STYLES.title,
        align: 'left',
      },
      legend: {
        ...CHART_STYLES.legend('right', true),
        reversed: true,
      },
      tooltip: CHART_STYLES.tooltipWithSuffixAndTotal(1),
      navigation: CHART_STYLES.navigation,
      xAxis: [{
        labels: {
          ...CHART_STYLES.xAxisLabelsStyle,
          format: '{value.period}<br><span style="font-weight: 500; color: #00948E">{value.budget}</span>',
        },
        categories: [],
        useHtml: true,
        type: 'category',
        crosshair: true,
        scrollbar: {
          ...CHART_STYLES.scrollbar,
          enabled: false, // Temporarily disable scrollbar
        },
        min: 0,
        max: 13,
      }],
      yAxis: [{
        title: {
          enabled: false,
        },
        labels: CHART_STYLES.yAxisLabelsStyle,
        stackLabels: CHART_STYLES.grandTotalStackLabels({ includeSuffix: true }),
      }],
      plotOptions: {
        column: {
          stacking: 'normal',
          width: 100,
          dataLabels: CHART_STYLES.dataLabelsWithSuffix,
        },
      },
      exporting: CHART_STYLES.hiddenExportingButton,
      credits: {
        enabled: false,
      },
      series: [],
      lang: CHART_STYLES.lang,
    };

    this.options = { ...defaultOptions, ...this.additionalOptions };

    this.enableScrollBar();
    this.checkEmptyState();
  }

  private reRenderChart(): void {
    this.zone.runOutsideAngular(() => {
      if (document.getElementById(this.chartId)) {
        if (this.chartInstance) {
          this.chartInstance.destroy();
        }
        this.chartInstance = Highcharts.chart(this.chartId, this.options);
      }
    });
  }

  private renderChart(): void {
    this.zone.runOutsideAngular(() => {
      if (document.getElementById(this.chartId)) {
        this.chartInstance = Highcharts.chart(this.chartId, this.options);
      }
    });
  }

  private enableScrollBar(): void {
    const maxLabels = 14;
    const totalCategories = this.options.xAxis[0]?.categories?.length || 0;

    if (totalCategories > maxLabels) {
      this.options.xAxis[0].scrollbar.enabled = true;
      this.options.xAxis[0].min = Math.max(0, totalCategories - maxLabels);
      this.options.xAxis[0].max = totalCategories - 1;
    } else {
      this.options.xAxis[0].scrollbar.enabled = false;
      this.options.xAxis[0].min = null;
      this.options.xAxis[0].max = null;
    }
  }

  private setupSync(): void {
    this.chartSyncService.chartSync$
      .pipe(
        takeUntil(this.destroy$),
        takeUntil(this.unsubscribe$),
        exhaustMap((extremes) => {
          if (this.chartInstance && this.chartInstance.xAxis && this.chartInstance.xAxis[0]) {
            this.chartInstance.xAxis[0].setExtremes(extremes.min, extremes.max, true, false);
          }
          return [];
        }),
      )
      .subscribe();
  }

  private checkEmptyState(): void {
    this.showEmptyState = this.options.series.every((series) => !series.data || series.data.length === 0);
  }
}
